import React from 'react';
import { CustomLink } from 'src/components/CustomLink/CustomLink';
import { ShopNowButton } from 'src/components/ShopNowButton/ShopNowButton';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import camelcaseKeys from 'camelcase-keys';
import { usePathname } from 'src/hooks/router-hooks';
import { FooterNewsletterConnected } from 'src/components/Footer/FooterNewsletter';
import { SectionSkeleton } from 'src/elements/Skeletons/Skeletons';

import { LazyImg } from '../../hooks/useLazyLoad';
import { stripDomain } from '../../util/sanitizeLink';
import { altFromSrc, altFromUrl } from '../../util/ensureHtmlImageAlts';

import classes from './TrustedBy.scss';

export type TrustedByItem = {
    image?: string | null;
    link?: string | null;
};

type TrustedByProps = {
    items: TrustedByItem[];
};

const trustedByIntroWidget = gql`
    query {
        trustedByIntroWidget {
            title
            widget_parameters {
                title
                main_title
                description
                link
            }
        }
    }
`;

export const TrustedBy: React.FC<TrustedByProps> = ({ items }) => {
    const pathname = usePathname();
    const addNewsletter = pathname.includes('personalised-workwear');

    const trustedItems = [items.slice(0, 6), items.slice(6, 11)];

    const { loading, data } = useQuery(trustedByIntroWidget, { ssr: false });

    if (loading || !data) return <SectionSkeleton height="240px" />;

    const trustedByData = camelcaseKeys(data?.trustedByIntroWidget?.[0], { deep: true })?.widgetParameters;

    return (
        <div className={classes.trustedBy}>
            <h2 className={classes.trustedByTitle}>{trustedByData?.mainTitle}</h2>
            <div className={classes.trustedByContainer}>
                <div className={classes.trustedByElements}>
                    {trustedItems.map((array, index) => (
                        <TrustedItem array={array} key={index} />
                    ))}
                </div>
                <div className={classes.trustedByDescription}>
                    <div>
                        <h3>{trustedByData?.title}</h3>
                        <p>{trustedByData?.description}</p>
                        <ShopNowButton link={trustedByData?.link} />
                    </div>
                </div>
            </div>
            {addNewsletter && <FooterNewsletterConnected mobileOnly />}
        </div>
    );
};

const TrustedItem = ({ array }: { array: TrustedByItem[] }) => (
    <>
        {array.map((item, childIndex) => (
            <CustomLink link={stripDomain(item.link)} key={childIndex} className={classes.trustedByLink}>
                {item.image && (
                    <LazyImg
                        src={item.image}
                        alt={altFromUrl(item.link || '') || altFromSrc(item.image) || 'Brand logo'}
                    />
                )}
            </CustomLink>
        ))}
    </>
);
